<template>
  <Drawer
    :on-close="closePanel"
    :close-title="I18n.t('editor.modal_close')"
    :close-icon-position="closeIconPosition"
    :class="designDrawerStyles"
    container-padding-class="!py-4 sm:p-6 scrollable-drawer"
  >
    <template #content>
      <VerticalScrollContentContainer>
        <div class="drawer-mobile-padding flex flex-col gap-3 sm:gap-2 w-full">
          <CanvaCtaNew
            :image="store.selectedImage"
            :persistentStore="props.persistentStore"
            :refreshAllHdLayers="props.refreshAllHdLayers"
            :stage="props.stage"
            :stageHd="props.stageHd"
            :getDataURL="props.getDataURL"
            :canvaVariant="CanvaVariant.INLINE_EDITOR_DEFAULT"
            :canvaCtaIdentifier="CanvaCTAIdentifier.ADD_TEXT"
            :icon="IconText"
            :label="I18n.t('canva_cta.flows.add_text_to_image')"
          />
          <CanvaCtaNew
            :image="store.selectedImage"
            :persistentStore="props.persistentStore"
            :refreshAllHdLayers="props.refreshAllHdLayers"
            :stage="props.stage"
            :stageHd="props.stageHd"
            :getDataURL="props.getDataURL"
            :canvaVariant="CanvaVariant.INLINE_EDITOR_DEFAULT"
            :canvaCtaIdentifier="CanvaCTAIdentifier.ADD_ELEMENTS"
            :icon="IconElements"
            :label="I18n.t('canva_cta.flows.add_elements')"
          />
          <CanvaCtaNew
            :image="store.selectedImage"
            :persistentStore="props.persistentStore"
            :refreshAllHdLayers="props.refreshAllHdLayers"
            :stage="props.stage"
            :stageHd="props.stageHd"
            :getDataURL="props.getDataURL"
            :canvaVariant="CanvaVariant.INLINE_EDITOR_DEFAULT"
            :canvaCtaIdentifier="CanvaCTAIdentifier.USE_TEMPLATE"
            :icon="IconDesign"
            :label="I18n.t('canva_cta.flows.use_template')"
          />
        </div>
        <div class="flex flex-col gap-2 sm:pt-3 hide-on-tablet">
          <CanvaContentImage
            :image="store.selectedImage"
            :src="designContentImage"
            alt="Add text or elements to your image in Canva"
            :getDataURL="props.getDataURL"
            :canvaVariant="CanvaVariant.INLINE_EDITOR_DEFAULT"
            :canvaCtaIdentifier="CanvaCTAIdentifier.ADD_TEXT_VISUAL"
            :stage="props.stage"
            :stageHd="props.stageHd"
            :persistentStore="props.persistentStore"
            :refreshAllHdLayers="props.refreshAllHdLayers"
          />
        </div>
      </VerticalScrollContentContainer>
      <div class="mobile-asterisk">
        <hr class="divider flex" />
        <p class="text-typo-secondary text-xs text-center mt-2 mb-1 !px-4" v-html="tos_html" />
      </div>
    </template>
  </Drawer>
</template>

<script setup lang="ts">
import "@/src/i18n";

import { computed, inject } from "vue";

import { Drawer, ElementsIcon as IconElements } from "prism";

import CanvaCtaNew from "@/components/canva_cta_new.vue";

import { useEditorStore } from "@/stores/editor_store";
import { Stage } from "konva/lib/Stage";
import { CanvaCTAIdentifier, CanvaVariant } from "@/composables/canva_link_builder";
import designContentImage from "@assets/images/inline-editor/design_panel_content.png";
import IconText from "@/components/upload/editor_components/side_panel_icons/icon-text.vue";
import IconDesign from "@/components/upload/editor_components/side_panel_icons/icon-design.vue";
import CanvaContentImage from "@/components/upload/canva_content_image.vue";
import { I18n as I18nType } from "i18n-js";
import { PersistentStore } from "@/stores/persistent_store";
import { Image } from "@/modules/internal_api/image";
import { usePhotoEditor } from "@/composables/photo_editor";
import VerticalScrollContentContainer from "@/components/prism/vertical_scroll_content_container.vue";

const I18n: I18nType = inject("I18n");

const store = useEditorStore();

interface DesignPanelProps {
  editor: HTMLElement; // TODO look into removing
  stage: Stage;
  stageHd: Promise<Stage>;
  persistentStore: PersistentStore;
  refreshAllHdLayers: (image: Image) => Promise<void>;
  getDataURL: Function;
}

const { isNewEditorEnabled: isNewPhotoEditor } = usePhotoEditor();
const props = defineProps<DesignPanelProps>();

const tos_html = I18n.t("canva_cta.common.tos_link_html", { link: "https://www.canva.com/policies/terms-of-use/" });

const emit = defineEmits<{
  (e: "closePanel"): void;
}>();

const closePanel = () => {
  emit("closePanel");
};

const closeIconPosition = computed(() => {
  return isNewPhotoEditor.value ? "none" : "floating";
});

const designDrawerStyles = computed(() => {
  return isNewPhotoEditor.value ? "" : "designDrawer";
});
</script>

<style scoped lang="scss">
.designDrawer {
  @media (min-width: 1024px) {
    width: 24.25rem;
  }
}

.drawer-mobile-padding {
  @media (max-width: 767px) {
    padding: 0 0.75rem;
  }
}

.mobile-asterisk {
  @media (min-width: 1024px) {
    display: none;
  }
}
</style>
